import { Component, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContentAreaComponent } from "src/app/components/content-area/content-area.component";
import { TableComponent } from "src/app/components/table/table.component";
import { IActionClick, IPaginateEvent, ISortChange } from 'src/app/interfaces/components/table.interface';
import { SelectComponent } from "src/app/components/select/select.component";
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from "src/app/components/input/input.component";
import { Router } from '@angular/router';
import { TContentAreaAction } from 'src/app/interfaces/components/content-area.interface';
import { FilterAreaComponent } from "src/app/components/filter-area/filter-area.component";
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import {
  contentAreaActions,
  displayedColumns,
  searchOptions,
  tableLegends,
} from './employee-list.constants';
import { statusOptions } from 'src/app/shared/constants/status-select.constants';
import { AlertService } from 'src/app/services/alert.service';
import { ExportService } from 'src/app/services/export.service';
import { EmployeeService } from 'src/app/services/employee/employee.service';
import { TFilterAction } from 'src/app/interfaces/components/filter.interface';
import { IEmployeeTable } from 'src/app/interfaces/employee/employee.interface';
import { PersonService } from 'src/app/services/person/person.service';

@Component({
  selector: 'app-employee-list',
  standalone: true,
  imports: [
    ContentAreaComponent,
    TableComponent,
    TranslateModule,
    SelectComponent,
    InputComponent,
    FilterAreaComponent,
    ReactiveFormsModule
  ],
  templateUrl: './employee-list.component.html',
})
export class EmployeeListComponent {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(AlertService);
  private readonly exportService = inject(ExportService);
  private readonly employeeService = inject(EmployeeService);
  private readonly personService = inject(PersonService);

  contentAreaActions = contentAreaActions;
  statusOptions = statusOptions;
  searchOptions = searchOptions;
  dataList: IEmployeeTable[] = [];
  tableLegends = tableLegends;
  displayedColumns = displayedColumns;
  length = 0;

  form: FormGroup = this.employeeService.formFilterList;

  ngOnInit(): void {
    this.handlePaginatorClick();
  }

  get pageIndex() {
    return this.form.get('page')?.value;
  }

  get pageSize() {
    return this.form.get('pageSize')?.value;
  }

  get filterPayload() {
    return {
      document: this.form.get('document')?.value,
      name: this.form.get('name')?.value,
      email: this.form.get('email')?.value,
      status: this.form.get('status')?.value,
      page: this.form.get('page')?.value,
      pageSize: this.form.get('pageSize')?.value,
      sortBy: this.form.get('sortBy')?.value,
      sortOrder: this.form.get('sortOrder')?.value,
    };
  }

  onSortChange(event: ISortChange) {
    this.form.get('sortBy')?.setValue(event.sortField);
    this.form.get('sortOrder')?.setValue(event.sortDirection);
    this.getTableData();
  }

  handlePaginatorClick(event?: IPaginateEvent) {
    if (event) {
      this.form.get('pageSize')?.setValue(event.pageSize);
      this.form.get('page')?.setValue(event.page);
    } else {
      this.form.get('pageSize')?.setValue(5);
      this.form.get('page')?.setValue(1);
    }
    this.getTableData();
  }

  getTableData() {
    const payload = this.filterPayload;

    this.employeeService.listAll(payload).subscribe({
      next: res => {
        const dataList = res.items.map((item: IEmployeeTable) => {
          return {
            ...item,
            tableStatus: item.status ? 'Ativo' : 'Inativo',
          };
        });
        this.dataList = dataList;
        this.length = res.totalItems;
      },
      error: (error) => {
        this.alertService.show({
          type: 'error',
          subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_TABLE_MESSAGE_ERROR'
        });
      }
    });
  }

  handleContentAreaEvent(event: TContentAreaAction) {
    if (event === 'add') {
      this.redirectForm();
    }
    if (event === 'export') {
      this.export();
    }
  }

  handleEmitterEventActionClick(event: IActionClick<any>) {
    const row = event.row;
    const id = row.id;
    const name = row.name;

    if (event.type === 'edit') {
      this.redirectForm(id);
    }

    if (event.type === 'delete') {
      this.delete(id, name);
    }

    if (event.type === 'toggle') {
      this.toggle(row);
    }
  }

  handleFilterAreaEvent(event: TFilterAction) {
    if (event === 'cleanFilters') {
      this.form.reset();
    }

    this.handlePaginatorClick();
  }

  redirectForm(id?: string) {
    if (id) {
      this.router.navigate([`/panel-master/employees/form/${id}`]);
    } else {
      this.router.navigate(['/panel-master/employees/form']);
    }
  }

  export() {
    const payload = this.filterPayload;

    this.employeeService.export(payload).subscribe({
      next: (blob) => {
        this.exportService.downloadBlob(
          blob,
          'funcionários.xlsx'
        );
      },
      error: (error) => {
        this.alertService.show({
          type: 'error',
          subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
        });
      }
    });
  }

  delete(id: string, name: string) {
    const subtitle = this.translate.instant('EMPLOYEE_LIST.MODAL.SUBTITLE_DELETE', { name });

    this.dialog.open(ModalComponent, {
      data: {
        type: 'delete',
        subtitle: subtitle,
      },
    })
    .afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.personService.delete(id).subscribe({
          next: () => {
            this.alertService.show({
              type: 'success',
              subtitle: 'ALERT_MODAL.DEFAULT_MESSAGE_SUCCESS'
            });
            this.getTableData();
          },
          error: (error) => {
            this.alertService.show({
              type: 'error',
              subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
            });
          }
        });
      }
    });
  }

  toggle(row: any) {
    const id = row.id;
    const name = row.name;
    const status = row.status;

    let subtitle = '';

    if (status === true) {
      subtitle = this.translate.instant('EMPLOYEE_LIST.MODAL.SUBTITLE_TOGGLE_INACTIVE', { name });
    } else {
      subtitle = this.translate.instant('EMPLOYEE_LIST.MODAL.SUBTITLE_TOGGLE_ACTIVE', { name });
    }

    this.dialog.open(ModalComponent, {
      data: {
        type: 'toggle',
        subtitle: subtitle,
      },
    })
    .afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.personService.toggle(id, !status).subscribe({
          next: () => {
            this.getTableData();
          },
          error: (error) => {
            this.alertService.show({
              type: 'error',
              subtitle: error?.error?.message || 'ALERT_MODAL.DEFAULT_MESSAGE_ERROR'
            });
          }
        });
      }
    });
  }
}
